if (!customElements.get('byte-footer')) {
  class ByteFooter extends HTMLElement {
    constructor() {
      super();

      this.desktopMediaQuery = window.matchMedia('(min-width: 750px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleMediaChange = this.handleMediaChange.bind(this);
      this.animationDuration = 320;
    }

    connectedCallback() {
      this.#cacheElements();
      this.#syncColumns();
      this.#bindEvents();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      const summary = event.target.closest('[data-footer-summary]');
      if (!summary) return;

      if (this.desktopMediaQuery.matches) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      const column = summary.closest('[data-footer-column]');
      if (!column || column.dataset.animating === 'true') return;

      if (column.open) {
        this.#closeColumn(column);
      } else {
        this.#openColumn(column);
      }
    }

    handleMediaChange() {
      this.#syncColumns();
    }

    #cacheElements() {
      this.columns = this.querySelectorAll('[data-footer-column]');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      this.desktopMediaQuery.addEventListener('change', this.boundHandleMediaChange);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      this.desktopMediaQuery.removeEventListener('change', this.boundHandleMediaChange);
    }

    #syncColumns() {
      const isDesktop = this.desktopMediaQuery.matches;

      for (const column of this.columns) {
        column.dataset.animating = 'false';
        this.#clearPanelStyles(column);
        column.open = isDesktop;
      }
    }

    #openColumn(column) {
      const panel = column.querySelector('[data-footer-panel]');

      if (!panel || this.reducedMotionQuery.matches) {
        column.open = true;
        return;
      }

      column.dataset.animating = 'true';
      column.open = true;
      panel.style.height = '0px';
      panel.style.opacity = '0';
      panel.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.transition = `height ${this.animationDuration}ms ease, opacity ${this.animationDuration}ms ease`;
          panel.style.height = `${panel.scrollHeight}px`;
          panel.style.opacity = '1';
        });
      });

      this.#onPanelTransitionEnd(panel, () => {
        this.#clearPanelStyles(column);
        column.dataset.animating = 'false';
      });
    }

    #closeColumn(column) {
      const panel = column.querySelector('[data-footer-panel]');

      if (!panel || this.reducedMotionQuery.matches) {
        column.open = false;
        return;
      }

      column.dataset.animating = 'true';
      panel.style.height = `${panel.scrollHeight}px`;
      panel.style.opacity = '1';
      panel.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.transition = `height ${this.animationDuration}ms ease, opacity ${this.animationDuration}ms ease`;
          panel.style.height = '0px';
          panel.style.opacity = '0';
        });
      });

      this.#onPanelTransitionEnd(panel, () => {
        column.open = false;
        this.#clearPanelStyles(column);
        column.dataset.animating = 'false';
      });
    }

    #onPanelTransitionEnd(panel, callback) {
      const handleTransitionEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'height') return;

        panel.removeEventListener('transitionend', handleTransitionEnd);
        callback();
      };

      panel.addEventListener('transitionend', handleTransitionEnd);
    }

    #clearPanelStyles(column) {
      const panel = column.querySelector('[data-footer-panel]');
      if (!panel) return;

      panel.style.height = '';
      panel.style.opacity = '';
      panel.style.overflow = '';
      panel.style.transition = '';
    }
  }

  customElements.define('byte-footer', ByteFooter);
}
